import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createTextNode, $getNearestNodeFromDOMNode, $getRoot, LineBreakNode } from 'lexical';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import SpellCheckerTooltip from './SpellCheckerTooltip';
import {
  $createMisspelledNode,
  $isMisspelledNode,
  $restoreSelectionFromOffset,
  $saveSelectionAsOffset,
  findNodeAtPosition,
  getAllDescendants,
  replaceMisspelledWithTextNodes,
  transformApiMatches,
} from './utils';

// Splits a TextNode at the mistake's position, inserting a MisspelledNode in the middle
function splitAndMarkMisspelled(editor, mistake, targetNode, targetOffset) {
  const { length, suggestions, reason } = mistake;
  const nodeText = targetNode.getTextContent();

  if (targetOffset > 0) {
    const beforeText = nodeText.substring(0, targetOffset);
    const misspelledText = nodeText.substring(targetOffset, targetOffset + length);
    const afterText = nodeText.substring(targetOffset + length);

    const misspelledNode = $createMisspelledNode(misspelledText);
    misspelledNode.setSuggestions(suggestions);
    misspelledNode.setReason(reason);

    if (beforeText) {
      targetNode.insertBefore($createTextNode(beforeText));
    }

    targetNode.insertBefore(misspelledNode);

    if (afterText) {
      targetNode.insertBefore($createTextNode(afterText));
    }

    targetNode.remove();
  } else {
    const misspelledText = nodeText.substring(0, length);
    const afterText = nodeText.substring(length);

    const misspelledNode = $createMisspelledNode(misspelledText);
    misspelledNode.setSuggestions(suggestions);
    misspelledNode.setReason(reason);

    targetNode.insertBefore(misspelledNode);

    if (afterText) {
      targetNode.insertBefore($createTextNode(afterText));
    }

    targetNode.remove();
  }
}

function hasTextContentChanged(currentLines, lastLines) {
  if (currentLines.length !== lastLines.length) return true;
  for (let i = 0; i < currentLines.length; i++) {
    if (currentLines[i] !== lastLines[i]) return true;
  }
  return false;
}

function SpellCheckerPlugin({ checkSpelling, oneLineMode, debounceDelay = 3000 }) {
  const [editor] = useLexicalComposerContext();
  const [tooltip, setTooltip] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const selectedNodeKeyRef = useRef(null);
  const tooltipPinnedRef = useRef(false);
  const tooltipRef = useRef(null);
  const lastCheckedLinesRef = useRef([]);
  const [tooltipInteractionActive, setTooltipInteractionActive] = useState(false);
  const [ignoredWords, setIgnoredWords] = useState(new Set());
  const ignoredWordsRef = useRef(new Set());
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const isEditorFocusedRef = useRef(false);
  const debounceTimerRef = useRef(null);

  // Sync state -> refs so async callbacks always read the latest values
  useEffect(() => {
    ignoredWordsRef.current = ignoredWords;
  }, [ignoredWords]);

  useEffect(() => {
    isEditorFocusedRef.current = isEditorFocused;
  }, [isEditorFocused]);

  const getEditorText = useCallback(() => {
    return editor.getEditorState().read(() => $getRoot().getTextContent());
  }, [editor]);

  const processSpellCheckResults = useCallback(
    (mistakes) => {
      const currentIgnored = ignoredWordsRef.current;
      const filteredMistakes = mistakes.filter((mistake) => !currentIgnored.has(mistake.word.toLowerCase()));

      editor.update(
        () => {
          // Save cursor as absolute offset before replacing nodes (node replacement destroys selection)
          const savedSelection = $saveSelectionAsOffset();

          const root = $getRoot();
          replaceMisspelledWithTextNodes(root);

          if (filteredMistakes.length > 0) {
            // Process right-to-left so earlier positions aren't invalidated by node splits
            filteredMistakes
              .sort((a, b) => b.position - a.position)
              .forEach((mistake) => {
                const { position } = mistake;
                const nodeInfo = findNodeAtPosition($getRoot(), position);

                if (nodeInfo) {
                  splitAndMarkMisspelled(editor, mistake, nodeInfo.node, nodeInfo.offset);
                }
              });
          }

          $restoreSelectionFromOffset(savedSelection);
        },
        // tag prevents our update listener from re-triggering spellcheck; discrete forces sync DOM flush
        { tag: 'spellcheck', discrete: true }
      );
    },
    [editor]
  );

  const triggerSpellCheck = useCallback(async () => {
    const currentContent = getEditorText();

    if (!currentContent.trim()) return;

    const lines = currentContent.split('\n');
    const linesToCheck = lines.filter((line) => line.length > 0);

    if (!hasTextContentChanged(linesToCheck, lastCheckedLinesRef.current)) return;

    // Write ref synchronously BEFORE await to prevent concurrent invocations
    lastCheckedLinesRef.current = linesToCheck;

    // Snapshot text for race condition detection
    const textSnapshot = currentContent;

    const lineInfo = [];
    let currentOffset = 0;

    lines.forEach((line) => {
      if (line.length > 0) {
        lineInfo.push({
          text: line,
          offset: currentOffset,
        });
      }
      currentOffset += line.length + 1; // +1 for \n between lines
    });

    const checkPromises = lineInfo.map(async ({ text, offset }) => {
      const matches = await checkSpelling(text);
      const lineMistakes = transformApiMatches(matches, text);
      return { mistakes: lineMistakes, offset };
    });

    const results = await Promise.all(checkPromises);

    // Race condition guard: if text changed during async calls, discard stale results
    const currentText = getEditorText();
    if (currentText !== textSnapshot) return;

    let allMistakes = [];
    results.forEach((result) => {
      const { mistakes, offset } = result;
      const adjustedMistakes = mistakes.map((mistake) => ({
        ...mistake,
        position: mistake.position + offset,
      }));
      allMistakes = [...allMistakes, ...adjustedMistakes];
    });

    processSpellCheckResults(allMistakes);
  }, [checkSpelling, getEditorText, processSpellCheckResults]);

  // Store handler refs for stable event listeners (refs updated every render, wrappers registered once)
  const handleEditorUpdateRef = useRef(null);
  const handleWordMouseOverRef = useRef(null);
  const handleWordMouseOutRef = useRef(null);
  const handleWordClickRef = useRef(null);
  const handleBlurRef = useRef(null);
  const handleFocusRef = useRef(null);
  const handleClickOutsideRef = useRef(null);

  const closeTooltip = useCallback(() => {
    setTooltip(null);
    setSelectedNode(null);
    selectedNodeKeyRef.current = null;
    tooltipPinnedRef.current = false;
    setTooltipInteractionActive(false);
  }, []);

  const getTooltipDataFromElement = (element) => {
    if (!element || element.getAttribute('data-misspelled') !== 'true') {
      return null;
    }

    const rect = element.getBoundingClientRect();
    const suggestions = JSON.parse(element.getAttribute('data-suggestions') || '[]');
    const reason = element.getAttribute('data-reason') || 'Misspelled word';

    return {
      element,
      data: {
        word: element.textContent,
        suggestions,
        reason,
        position: {
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          wordTop: rect.top + window.scrollY,
        },
      },
    };
  };

  const showTooltipForElement = (element) => {
    const tooltipData = getTooltipDataFromElement(element);
    if (!tooltipData) return;

    // Bridge from DOM -> Lexical node tree to get the stable node key
    let nodeKey = null;
    editor.read(() => {
      const lexicalNode = $getNearestNodeFromDOMNode(element);
      if (lexicalNode) {
        nodeKey = lexicalNode.getKey();
      }
    });

    setTooltipInteractionActive(true);
    setTooltip(tooltipData.data);
    setSelectedNode(tooltipData.element);
    selectedNodeKeyRef.current = nodeKey;
  };

  handleWordMouseOverRef.current = (e) => {
    const target = e.target;
    if (target.getAttribute('data-misspelled') === 'true') {
      // Don't reset a click-pinned tooltip for the same word
      if (tooltipPinnedRef.current && selectedNode === target) return;
      tooltipPinnedRef.current = false;
      showTooltipForElement(target);
    }
  };

  handleWordClickRef.current = (e) => {
    const target = e.target;

    if (target.getAttribute('data-misspelled') === 'true') {
      // Only toggle-close if tooltip was previously pinned by click
      if (tooltipPinnedRef.current && selectedNode === target && tooltip) {
        closeTooltip();
        return;
      }
      showTooltipForElement(target);
      tooltipPinnedRef.current = true;
    } else if (tooltip) {
      closeTooltip();
    }
  };

  handleWordMouseOutRef.current = (e) => {
    if (e.target.getAttribute('data-misspelled') === 'true' && !tooltipPinnedRef.current) {
      setTooltipInteractionActive(false);
    }
  };

  handleClickOutsideRef.current = (event) => {
    if (tooltipRef.current && !tooltipRef.current.contains(event.target) && (!selectedNode || !selectedNode.contains(event.target))) {
      closeTooltip();
    }
  };

  handleFocusRef.current = () => {
    setIsEditorFocused(true);
  };

  handleBlurRef.current = (e) => {
    const isTooltipTarget = tooltipRef.current && (tooltipRef.current.contains(e.relatedTarget) || tooltipInteractionActive);

    if (!isTooltipTarget) {
      setIsEditorFocused(false);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      triggerSpellCheck();
    }
  };

  handleEditorUpdateRef.current = () => {
    if (!isEditorFocusedRef.current) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      triggerSpellCheck();
    }, debounceDelay);
  };

  const handleSuggestionClick = (suggestion) => {
    if (!tooltip || !selectedNode) return;

    setTooltipInteractionActive(true);
    const targetNodeKey = selectedNodeKeyRef.current;

    editor.update(
      () => {
        const root = $getRoot();
        let replaced = false;

        // Match by node key if available, fall back to text match
        if (targetNodeKey) {
          getAllDescendants(root, (node) => {
            if (replaced) return;
            if ($isMisspelledNode(node) && node.getKey() === targetNodeKey) {
              const textNode = $createTextNode(suggestion);
              node.replace(textNode);
              replaced = true;
            }
          });
        }

        if (!replaced) {
          getAllDescendants(root, (node) => {
            if (replaced) return;
            if ($isMisspelledNode(node) && node.getTextContent() === tooltip.word) {
              const textNode = $createTextNode(suggestion);
              node.replace(textNode);
              replaced = true;
            }
          });
        }
      },
      { tag: 'spellcheck', discrete: true }
    );

    setTimeout(() => {
      const updatedContent = getEditorText();
      const updatedLines = updatedContent.split('\n').filter((line) => line.length > 0);
      lastCheckedLinesRef.current = updatedLines;
      closeTooltip();
    }, 100);
  };

  const handleIgnoreWord = (word) => {
    if (!word) return;

    setIgnoredWords((prev) => {
      const newSet = new Set(prev);
      newSet.add(word.toLowerCase());
      return newSet;
    });

    closeTooltip();

    editor.update(
      () => {
        const root = $getRoot();
        getAllDescendants(root, (node) => {
          if ($isMisspelledNode(node) && node.getTextContent().toLowerCase() === word.toLowerCase()) {
            const textNode = $createTextNode(node.getTextContent());
            node.replace(textNode);
          }
        });
      },
      { tag: 'spellcheck', discrete: true }
    );
  };

  // Stable event listeners — register once, wrappers read .current at call-time for fresh closures
  useEffect(() => {
    const editorElement = editor.getRootElement();
    if (!editorElement) return;

    const onMouseOver = (e) => handleWordMouseOverRef.current(e);
    const onMouseOut = (e) => handleWordMouseOutRef.current(e);
    const onClick = (e) => handleWordClickRef.current(e);
    const onBlur = (e) => handleBlurRef.current(e);
    const onFocus = (e) => handleFocusRef.current(e);
    const onClickOutside = (e) => handleClickOutsideRef.current(e);

    editorElement.addEventListener('mouseover', onMouseOver);
    editorElement.addEventListener('mouseout', onMouseOut);
    editorElement.addEventListener('click', onClick);
    editorElement.addEventListener('blur', onBlur, true);
    editorElement.addEventListener('focus', onFocus, true);
    document.addEventListener('mousedown', onClickOutside);

    return () => {
      editorElement.removeEventListener('mouseover', onMouseOver);
      editorElement.removeEventListener('mouseout', onMouseOut);
      editorElement.removeEventListener('click', onClick);
      editorElement.removeEventListener('blur', onBlur, true);
      editorElement.removeEventListener('focus', onFocus, true);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [editor]);

  // Intercept every LineBreakNode creation and remove it, enforcing single-line input
  useEffect(() => {
    if (oneLineMode) {
      return editor.registerNodeTransform(LineBreakNode, (node) => {
        node.remove();
      });
    }
  }, [editor, oneLineMode]);

  const handleTooltipMouseLeave = () => {
    setTooltipInteractionActive(false);
  };

  const handleTooltipMouseEnter = () => {
    setTooltipInteractionActive(true);
  };

  useEffect(() => {
    if (tooltip && !tooltipInteractionActive && !tooltipPinnedRef.current) {
      const timer = setTimeout(() => {
        closeTooltip();
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [tooltipInteractionActive, tooltip, closeTooltip]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Fires on every editor state change; skip spellcheck-tagged updates to avoid re-triggering ourselves
  useEffect(() => {
    return editor.registerUpdateListener(({ tags }) => {
      if (tags.has('spellcheck')) return;
      handleEditorUpdateRef.current();
    });
  }, [editor]);

  return (
    <>
      {tooltip &&
        createPortal(
          <SpellCheckerTooltip
            tooltip={tooltip}
            tooltipRef={tooltipRef}
            onSuggestionClick={handleSuggestionClick}
            onIgnoreWord={handleIgnoreWord}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleTooltipMouseLeave}
            onClose={closeTooltip}
          />,
          document.body
        )}
    </>
  );
}

export default SpellCheckerPlugin;
